import logger from '../../Utils/logger';
import {
  Card,
  Redirect,
  Klarna,
  SepaDirectDebit,
  PayNow,
  WeChat,
  GooglePay,
  ApplePay,
} from "@adyen/adyen-web";

import cardIcon from "../../Assets/Images/payments/Card.svg";
import alipayIcon from "../../Assets/Images/payments/Alipay.svg";
import googlepayIcon from "../../Assets/Images/payments/GooglePay.svg";
import applepayIcon from "../../Assets/Images/payments/ApplePay.svg";
import wechatIcon from "../../Assets/Images/payments/WeChat.svg";
import paynowIcon from "../../Assets/Images/payments/paynow.svg";

/** Legacy keys used for sessionStorage locks & UI — keep stable for existing flows */
export const PAYMENT_METHODS = {
  CARD: "card",
  ALIPAY: "alipay",
  WECHATPAY: "wechatpay",
  WECHATPAY_WEB: "wechatpayWeb",
  PAYNOW: "paynow",
  GOOGLEPAY: "googlepay",
  APPLEPAY: "applepay",
};

/** Adyen types mounted in the express row (not in accordion) */
export const EXPRESS_ADYEN_TYPES = new Set(["applepay", "googlepay", "paywithgoogle"]);

const DEFAULT_ICON = cardIcon;

/** Labels / descriptions when API does not send `name` */
const TYPE_META = {
  scheme: { label: "Cards", description: "All fields are required unless marked otherwise." },
  ideal: { label: "iDEAL", description: "Pay with your Dutch bank" },
  klarna: { label: "Klarna", description: "Pay with Klarna" },
  klarna_account: { label: "Klarna", description: "Pay later with Klarna" },
  klarna_paynow: { label: "Klarna", description: "Pay now with Klarna" },
  klarna_b2b: { label: "Klarna", description: "Klarna for business" },
  sepadirectdebit: { label: "SEPA Direct Debit", description: "Euro bank debit" },
  wechatpayWeb: { label: "WeChat Pay", description: "Pay with WeChat" },
  wechatpayQR: { label: "WeChat Pay", description: "Scan with WeChat" },
  paynow: { label: "PayNow", description: "Singapore PayNow" },
  alipay: { label: "Alipay", description: "Pay with Alipay" },
};

const TYPE_ICONS = {
  scheme: cardIcon,
  alipay: alipayIcon,
  wechatpayWeb: wechatIcon,
  wechatpayQR: wechatIcon,
  paynow: paynowIcon,
  ideal: DEFAULT_ICON,
  klarna: DEFAULT_ICON,
  klarna_account: DEFAULT_ICON,
  klarna_paynow: DEFAULT_ICON,
  klarna_b2b: DEFAULT_ICON,
  sepadirectdebit: DEFAULT_ICON,
};

/**
 * Map Adyen `paymentMethods[].type` to internal lock/session keys (matches legacy PAYMENT_METHODS values).
 */
export function toInternalMethodKey(adyenType) {
  if (adyenType === "scheme") return PAYMENT_METHODS.CARD;
  if (adyenType === "wechatpayWeb" || adyenType === "wechatpayQR") return PAYMENT_METHODS.WECHATPAY;
  return adyenType;
}

/**
 * Adyen may return `wechatpayWeb` and/or `wechatpayQR` (sometimes multiple entries). They map
 * to the same internal key — keep a single row and prefer `wechatpayQR` so the shopper sees an
 * on-page QR code instead of redirecting to the WeChat / Adyen hosted web flow.
 */
function dedupeWechatPayVariants(paymentMethods) {
  const indices = [];
  paymentMethods.forEach((pm, i) => {
    if (!pm?.type) return;
    if (toInternalMethodKey(pm.type) === PAYMENT_METHODS.WECHATPAY) indices.push(i);
  });
  if (indices.length <= 1) return paymentMethods;

  /** Prefer QR (in-page scan) over web/redirect — matches WeChat Pay desktop UX. */
  const variantRank = (type) =>
    type === "wechatpayQR" ? 0 : type === "wechatpayWeb" ? 1 : 2;

  let keepIdx = indices[0];
  let bestRank = variantRank(paymentMethods[keepIdx].type);
  for (let k = 1; k < indices.length; k++) {
    const i = indices[k];
    const r = variantRank(paymentMethods[i].type);
    if (r < bestRank) {
      bestRank = r;
      keepIdx = i;
    }
  }
  const drop = new Set(indices.filter((i) => i !== keepIdx));
  return paymentMethods.filter((_, i) => !drop.has(i));
}

/**
 * Build accordion rows from `/sessions` paymentMethodsResponse.
 */
export function buildPaymentMethodRows(paymentMethodsResponse) {
  const list = dedupeWechatPayVariants(paymentMethodsResponse?.paymentMethods ?? []);
  return list
    .map((pm, index) => {
      if (!pm?.type || EXPRESS_ADYEN_TYPES.has(pm.type)) return null;
      const internalKey = toInternalMethodKey(pm.type);
      const meta = TYPE_META[pm.type] || {};
      return {
        rowKey: `${pm.type}__${index}`,
        adyenType: pm.type,
        internalKey,
        label: pm.name || meta.label || formatTypeLabel(pm.type),
        description: meta.description || "",
        icon: TYPE_ICONS[pm.type] || DEFAULT_ICON,
        paymentMethod: pm,
      };
    })
    .filter(Boolean);
}

function formatTypeLabel(type) {
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Creates the Adyen component instance for a payment method row.
 * @returns {import('@adyen/adyen-web').UIElement | null}
 */
export function createPaymentComponent(checkout, row, ctx) {
  if (!checkout || !row?.paymentMethod) return null;

  const { paymentMethod, adyenType } = row;
  const {
    baseUrl,
    selectedStoredCard,
    onCardChange,
    onCardError,
  } = ctx;

  try {
    switch (adyenType) {
      case "scheme":
        return new Card(checkout, {
          ...paymentMethod,
          ...(selectedStoredCard ? selectedStoredCard : {}),
          hasHolderName: true,
          holderNameRequired: !selectedStoredCard,
          // ✅ Remove "Save for my next payment" checkbox completely
          storePaymentMethod: false,
          enableStoreDetails: false,
          showStorePaymentField: false,
          holderName: true,
          onChange: onCardChange,
          onError: onCardError,
        });

      case "sepadirectdebit":
        return new SepaDirectDebit(checkout, { ...paymentMethod });

      case "klarna":
      case "klarna_account":
      case "klarna_paynow":
      case "klarna_b2b":
        return new Klarna(checkout, { ...paymentMethod, type: adyenType });

      case "paynow":
        return new PayNow(checkout, {
          ...paymentMethod,
          returnUrl: `${window.location.origin}/payment/callback`,
        });

      case "wechatpayWeb":
      case "wechatpayQR":
        return new WeChat(checkout, {
          ...paymentMethod,
          type: adyenType === "wechatpayWeb" ? "wechatpayWeb" : "wechatpayQR",
          returnUrl: `${baseUrl}/payment/callback`,
        });

      case "alipay":
        return new Redirect(checkout, { ...paymentMethod, type: "alipay",
          returnUrl: `${baseUrl}/payment/callback` });

      default:
        return new Redirect(checkout, { ...paymentMethod, type: adyenType });
    }
  } catch (e) {
    logger.error(`createPaymentComponent failed for ${adyenType}`, e);
    return null;
  }
}

export function createApplePayComponent(checkout, ctx) {
  const { amount, currency } = ctx;
  return new ApplePay(checkout, {
    amount: { value: amount * 100, currency },
    countryCode: "SG",
    onClick: (resolve) => resolve(),
  });
}

export function createGooglePayComponent(checkout) {
  return new GooglePay(checkout, {
    supportedMethods: "https://google.com/pay",
    environment: "TEST",
    merchantInfo: {
      merchantId: process.env.REACT_APP_GOOGLE_MERCHANT_ID || "YOUR_GOOGLE_MERCHANT_ID",
      merchantName: "Your Merchant Name",
    },
    buttonType: "buy",
    allowedPaymentMethods: ["CARD", "TOKENIZED_CARD"],
  });
}

export const PAYMENT_IMAGES = {
  [PAYMENT_METHODS.CARD]: cardIcon,
  [PAYMENT_METHODS.ALIPAY]: alipayIcon,
  [PAYMENT_METHODS.WECHATPAY]: wechatIcon,
  [PAYMENT_METHODS.PAYNOW]: paynowIcon,
  [PAYMENT_METHODS.GOOGLEPAY]: googlepayIcon,
  [PAYMENT_METHODS.APPLEPAY]: applepayIcon,
};
